// new Filter('test', 5).and('test', 5).or('test', 5).and('x', 3).or('t', 1').or('x', 3);
// new Filter('test', 5').nest('test', 'x).or('x', 'x').unnest().and('X', 1)    = TEST = 5 AND (test = x or x = x) AND X = 1

import {compareStrings, inArray, isEmptyObject} from './utils';

export class Filter
{
    protected _ands: [FilterAnds[]] = [[]];

    protected lastAnd: number;

    protected parent: Nullable<Filter>;

    static createByObject(obj: object): Nullable<Filter>
    {
        let filter: Nullable<Filter> = null;
        for (let key in obj)
        {
            if (obj.hasOwnProperty(key))
            {
                if('' === obj[key])
                {
                    continue;
                }
                if (filter === null)
                {
                    filter = new Filter(key, obj[key]);
                }
                else
                {
                    filter.and(key, obj[key]);
                }
            }
        }

        return filter;
    }

    constructor(field: string, value: any)
    {
        this.lastAnd = 0;
        this.and(field, value);
    }

    private _add(fieldString: string, value: any)
    {
        let fields = fieldString.split(',');
        if (fields.length > 1)
        {
            let nested = this.nest(fields[0], value);
            for (let field of fields.slice(1))
            {
                nested.or(field, value);
            }
        }
        else
        {
            this._ands[this.lastAnd].push({
                field: fieldString.trim().split('.'),
                value: typeof value === 'string' ? value.toLowerCase() : value,
                isArray: Array.isArray(value),
            });
        }

        return this;
    }

    and(field: string, value: any)
    {
        return this._add(field, value);
    }

    or(field: string, value: any)
    {
        this.lastAnd = this._ands.push([]) - 1;

        return this._add(field, value);
    }

    nest(field: string, value: any)
    {
        let newFilter    = new Filter(field, value);
        newFilter.parent = this;
        this._ands[this.lastAnd].push(newFilter);

        return newFilter;
    }

    unnest()
    {
        return this.parent;
    }

    filter(tmpEntries: object | [])
    {
        let entries = [];
        for (let entry of Array.isArray(tmpEntries) ? tmpEntries : Object.values(tmpEntries))
        {
            if (this.run(entry))
            {
                entries.push(entry);
            }
        }

        return entries;
    }

    run(obj: {})
    {
        let scanObj = obj;
        for (let and of this._ands)
        {
            let success: boolean = true;
            for (let expression of and)
            {
                if (expression instanceof Filter)
                {
                    success = expression.run(scanObj);
                }
                else
                {
                    if ('' === expression.value)
                    {
                        success = true;
                        continue;
                    }

                    let fieldName: string;
                    if (expression.field.length > 1)
                    {
                        scanObj = JSON.parse(JSON.stringify(obj));
                        for (let field of expression.field.slice(0, -1))
                        {
                            scanObj = scanObj[field];
                        }

                        fieldName = expression.field[expression.field.length - 1];
                    }
                    else
                    {
                        fieldName = expression.field[0];
                    }

                    if('undefined' === typeof scanObj)
                    {
                        success = false;
                        continue;
                    }

                    let fieldType = typeof scanObj[fieldName];

                    if (scanObj[fieldName] === null || typeof scanObj[fieldName] === 'object' && isEmptyObject(scanObj[fieldName]))
                    {
                        success = false;
                    }
                    else if (fieldType === 'string' || fieldType === 'number')
                    {
                        if (expression.isArray)
                        {
                            success = inArray(scanObj[fieldName], expression.value);
                        }
                        else
                        {
                            success = compareStrings(scanObj[fieldName], expression.value);
                        }
                    }
                    else if (fieldType === 'undefined' && scanObj !== null && (typeof scanObj === 'object' || Array.isArray(scanObj)))
                    {
                        let executed = false;
                        for (let key in scanObj)
                        {
                            if (scanObj.hasOwnProperty(key) && scanObj[key] && 'undefined' !== typeof scanObj[key][fieldName])
                            {
                                executed = true;
                                if (expression.isArray)
                                {
                                    success = inArray(scanObj[key][fieldName], expression.value);
                                }
                                else
                                {
                                    // TODO SPA: return empty array or skip filter?
                                    if (scanObj[key])
                                    {
                                        success = compareStrings(scanObj[key][fieldName], expression.value);
                                    }
                                    else
                                    {
                                        success = false;
                                    }
                                }

                                if (success)
                                {
                                    break;
                                }
                            }
                        }
                        if(!executed)
                        {
                            success = false;
                        }
                    }
                    else if (fieldType === 'object')
                    {
                        for (let value of Object.values(scanObj[fieldName]))
                        {
                            // todo move if/else-if to method and run the method (recursive)?
                            if (typeof value === 'string' || typeof value === 'number')
                            {
                                if (expression.isArray)
                                {
                                    success = inArray(value, expression.value);
                                }
                                else
                                {
                                    success = compareStrings(value, expression.value);
                                }

                                if (success)
                                {
                                    break;
                                }
                            }
                        }
                    }
                    else if (fieldType === 'boolean')
                    {
                        if (expression.isArray)
                        {
                            // '1', 1, true == true | '2', 2, 'a', false != true
                            success = expression.value.map((v: any) => v == true).includes(scanObj[fieldName]);
                        }
                        else
                        {
                            success = scanObj[fieldName] === !!expression.value;
                        }
                    }
                    else
                    {
                        console.error('invalid type', fieldName, scanObj[fieldName], fieldType);
                        success = false;
                    }
                }

                // break and
                if (!success)
                {
                    break;
                }
            }

            // break or
            if (success)
            {
                return true;
            }
        }

        return false;
    }
}

// export function orderBy(array: any[], predicate: string | string[], order: FilterOrder = 'asc'){
//     return array;
// }
export function orderBy(array: any[], predicate: string | string[], order: FilterOrder = 'asc')
{
    // let applyFilter = false;
    let reverseOrder: number = order === 'asc' ? 1 : -1;

    // if( sortPredicate && sortPredicate.indexOf( '|' ) !== -1 )
    // {
    //     var parts = sortPredicate.split( '|' );
    //     sortPredicate = parts.shift();
    //
    //     applyFilter = parts.join( '|' );
    // }

    //            var ts =(new Date()).getTime();
    if (!Array.isArray(array))
    {
        return array;
    }

    let sortPredicate: string[] = !Array.isArray(predicate) ? [predicate] : predicate;
    if (sortPredicate.length === 0)
    {
        return array;
    }

    let predicates = processPredicates(sortPredicate, reverseOrder);

    // Add a predicate at the end that evaluates to the element index. This makes the
    // sort stable as it works as a tie-breaker when all the input predicates cannot
    // distinguish between two elements.
    predicates.push({
        get: function()
        {
            return -1;
        },
        descending: reverseOrder ? -1 : 1,
    });

    // The next few lines are a version of a Swartzian Transform idiom from Perl
    // (sometimes called the Decorate-Sort-Undecorate idiom)
    // See https://en.wikipedia.org/wiki/Schwartzian_transform
    let compareValues = Array.prototype.map.call(array, getComparisonObject);
    compareValues.sort(doComparison);
    array = compareValues.map(function(item: any)
    {
        return item.value;
    });

    //            console.log(sortPredicate,(new Date()).getTime()-ts);
    return array;

    //<editor-fold desc="helper methods">
    function getComparisonObject(value: string, index: string | number)
    {
        return {
            value: value,
            predicateValues: predicates.map(function(predicate: any)
            {
                return getPredicateValue(predicate.get(value), index);
            }),
        };
    }

    function doComparison(a: any, b: any)
    {
        let result = 0;
        for (let index = 0, length = predicates.length; index < length; ++index)
        {
            result = compare(a.predicateValues[index], b.predicateValues[index]/*, applyFilter*/) * predicates[index].descending;
            if (result)
            {
                break;
            }
        }
        return result;
    }

    function naturalCompare(a: any, b: any)
    {
        let ax: any[] = [],
            bx: any[] = [];

        // if( applyFilter )
        // {
        //     var t = 'entry|' + applyFilter;
        //
        //     a = $parse( t )( {entry: a} );
        //     b = $parse( t )( {entry: b} );
        // }

        a.replace(/(\d+)|(\D+)/g, function(_: any, $1: any, $2: any)
        {
            ax.push([$1 || Infinity, $2 || '']);
        });
        b.replace(/(\d+)|(\D+)/g, function(_: any, $1: any, $2: any)
        {
            bx.push([$1 || Infinity, $2 || '']);
        });

        while (ax.length && bx.length)
        {
            let an = ax.shift();
            let bn = bx.shift();
            let nn = (an[0] - bn[0]) || an[1].localeCompare(bn[1]);
            if (nn)
            {
                return nn;
            }
        }

        return ax.length - bx.length;
    }

    function compare(a: any, b: any/*, applyFilter*/)
    {
        if ('number' === a.type || 'number' === b.type)
        {
            if ('undefined' === a.type)
            {
                a.type  = 'number';
                a.value = -1;
            }

            if ('undefined' === b.type)
            {
                b.type  = 'number';
                b.value = -1;
            }
        }

        if (a.type === b.type)
        {
            if (a.value !== b.value)
            {
                if ('string' === a.type)
                {
                    return naturalCompare(a.value, b.value);
                }

                return a.value < b.value ? -1 : 1;
            }
        }
        else
        {
            if ('' === a.value)
            {
                return -1;
            }
            if ('' === b.value)
            {
                return 1;
            }

            return a.type < b.type ? -1 : 1;
        }

        return 0;
    }

    function hasCustomToString(obj: any)
    {
        return 'string' === typeof obj.toString && obj.toString !== Object.prototype.toString;
    }

    function processPredicates(sortPredicate: any[], reverseOrder: number)
    {
        return sortPredicate.map(function(predicate: any)
        {
            let descending    = 1,
                ignoreReverse = false;

            if (predicate.charAt(0) == '!')
            {
                ignoreReverse = true;
                predicate     = predicate.substring(1);
            }
            if ((predicate.charAt(0) == '+' || predicate.charAt(0) == '-'))
            {
                descending = predicate.charAt(0) == '-' ? -1 : 1;
                predicate  = predicate.substring(1);
            }
            let get = function(value: any)
            {
                return value[predicate];
            };

            if (typeof predicate === 'string' && predicate.includes('.'))
            {
                get = function(value: any)
                {
                    return predicate.split('.').reduce((valueObject: any, currentKey: string) => valueObject?.[currentKey], value);
                };
            }

            return {
                get: get,
                descending: descending * (ignoreReverse ? 1 : reverseOrder),
            };
        });
    }

    function isPrimitive(value: any)
    {
        return ['number', 'boolean', 'string'].includes(typeof value);
    }

    function objectValue(value: any, index: string | number)
    {
        // If `valueOf` is a valid function use that
        if (typeof value.valueOf === 'function')
        {
            value = value.valueOf();
            if (isPrimitive(value))
            {
                return value;
            }
        }
        // If `toString` is a valid function and not the one from `Object.prototype` use that
        if (hasCustomToString(value))
        {
            value = value.toString();
            if (isPrimitive(value))
            {
                return value;
            }
        }
        // We have a basic object so we use the position of the object in the collection
        return index;
    }

    function getPredicateValue(value: any, index: string | number)
    {
        let type = typeof value;

        if (null === value || '' === value)
        {
            type  = 'string';
            value = '';
        }
        else if (type === 'string')
        {
            if (isFinite(value))
            {
                type  = 'number';
                value = parseInt(value) || value;
            }
            else
            {
                value = value.toLowerCase();
            }
        }
        else if (type === 'object')
        {
            value = objectValue(value, index);
        }
        return {
            value: value,
            type: type,
        };
    }

    //</editor-fold>
}